// src/components/Chat/ChatInput.tsx
// Input area — clean, compact, dengan quick actions yang rapi

import React, { useState, useRef, useCallback } from "react";

interface ChatInputProps {
  onSend: (text: string) => void;
  isLoading: boolean;
}

const QUICK_ACTIONS = [
  { icon: "📊", label: "Statistik", query: "Berapa jumlah wilayah tenang dan berisik saat ini?" },
  { icon: "📍", label: "Cari Tenang", query: "Tunjukkan di mana wilayah tenang?" },
  { icon: "🔊", label: "Cari Berisik", query: "Tunjukkan di mana wilayah berisik?" },
  { icon: "💡", label: "Tips", query: "Tips mengurangi paparan kebisingan" },
];

const ChatInput: React.FC<ChatInputProps> = ({ onSend, isLoading }) => {
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [showQuickActions, setShowQuickActions] = useState(true);

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    onSend(trimmed);
    setText("");
    setShowQuickActions(false);
    if (inputRef.current) inputRef.current.style.height = "auto";
  }, [text, isLoading, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 80)}px`;
    }
  };

  return (
    <div style={{ flexShrink: 0, borderTop: "1px solid rgba(74,144,226,0.15)", background: "#1e2139" }}>
      {/* Quick actions — horizontal scroll, tidak wrap */}
      {showQuickActions && (
        <div style={{
          display: "flex", gap: 6, padding: "8px 10px 4px",
          overflowX: "auto", flexWrap: "nowrap",
          scrollbarWidth: "none", msOverflowStyle: "none",
        }}>
          <style>{`.chat-quick-scroll::-webkit-scrollbar { display: none; }`}</style>
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.label}
              className="chat-quick-scroll"
              onClick={() => { onSend(a.query); setShowQuickActions(false); }}
              disabled={isLoading}
              style={{
                padding: "5px 12px", borderRadius: 14, fontSize: 11.5,
                border: "1px solid rgba(74,144,226,0.3)", background: "#252941",
                color: "#a0a8c0", cursor: isLoading ? "not-allowed" : "pointer",
                whiteSpace: "nowrap", outline: "none", display: "flex",
                alignItems: "center", gap: 4, lineHeight: 1.3,
                transition: "all 0.15s", flexShrink: 0,
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.background = "rgba(74,144,226,0.15)";
                  e.currentTarget.style.borderColor = "#4a90e2";
                  e.currentTarget.style.color = "#4a90e2";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#252941";
                e.currentTarget.style.borderColor = "rgba(74,144,226,0.3)";
                e.currentTarget.style.color = "#a0a8c0";
              }}
            >
              <span>{a.icon}</span> {a.label}
            </button>
          ))}
        </div>
      )}

      {/* Input row */}
      <div style={{
        display: "flex", alignItems: "flex-end", gap: 8,
        padding: "8px 10px 10px",
      }}>
        <textarea
          ref={inputRef}
          value={text}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder="Tanya tentang kebisingan..."
          rows={1}
          disabled={isLoading}
          style={{
            flex: 1, border: "1.5px solid rgba(74,144,226,0.3)", borderRadius: 18,
            padding: "8px 14px", fontSize: 13, outline: "none",
            resize: "none", minHeight: 18, maxHeight: 80,
            fontFamily: "inherit", lineHeight: 1.4, background: "#252941",
            color: "#e0e4f0",
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = "#4a90e2")}
          onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(74,144,226,0.3)")}
        />
        <button
          onClick={handleSend}
          disabled={!text.trim() || isLoading}
          title="Kirim"
          style={{
            width: 34, height: 34, borderRadius: "50%", border: "none",
            background: !text.trim() || isLoading
              ? "rgba(74,144,226,0.4)"
              : "linear-gradient(135deg, #4a90e2 0%, #357abd 100%)",
            color: "white",
            cursor: !text.trim() || isLoading ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, outline: "none",
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
